import { IncomingMessage } from 'http';

export const UNRESOLVED_IP_ADDRESS = '0.0.0.0';

type IpAddressType = {
    ipAddress: null | string;
};

const extractIpFromHeader = (ip: string): string | null => {
    const ipv4Regex = /(?<!::ffff:)\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g;
    const ipv6Regex =
        /(?:^|,\s*)([0-9A-Fa-f:]{2,}(?:%\w+)?|::ffff:\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?=\s*(?:,|$))/g;

    const ipv4Matches: RegExpMatchArray | [] = ip.match(ipv4Regex) || [];

    for (const ipv4 of ipv4Matches) {
        if (!ip.includes(`::ffff:${ipv4}`)) {
            const parts = ipv4.split('.').map(Number);
            if (parts.every((part) => part >= 0 && part <= 255)) {
                return ipv4; // Return first valid IPv4 immediately
            }
        }
    }

    const ipv6Matches = [];
    let match;
    while ((match = ipv6Regex.exec(ip)) !== null) {
        const ipv6Address = match[1].trim();
        if (ipv6Address) {
            ipv6Matches.push(ipv6Address);
        }
    }

    if (ipv6Matches.length > 0) {
        const ipv4Mapped = ipv6Matches.find((address) => address.startsWith('::ffff:'));
        if (ipv4Mapped) {
            return ipv4Mapped;
        }

        const linkLocalWithZone = ipv6Matches.find((addr) => addr.includes('%'));
        if (linkLocalWithZone) {
            return linkLocalWithZone;
        }

        const normalizedMatches = ipv6Matches.map((addr) => addr.toLowerCase());
        if (normalizedMatches.includes('1::1')) {
            return '1::1';
        }
        if (normalizedMatches.includes('::1')) {
            return '::1';
        }

        return ipv6Matches[0];
    }

    return null;
};

const getIpAddressFromRequest = (request: IncomingMessage | undefined): string => {
    const headers = [
        'fastly-client-ip',
        'x-cnet-forwarded-for',
        'x-original-forwarded-for',
        'x-forwarded-for',
    ];

    // Check headers for valid IPs
    if (request) {
        for (const header of headers) {
            const headerValue = request.headers[header] as string | undefined;
            if (headerValue) {
                const ip = extractIpFromHeader(headerValue);
                if (ip) {
                    return ip; // Return the first valid IP from header
                }
            }
        }
    }

    return request?.socket?.remoteAddress || UNRESOLVED_IP_ADDRESS;
};

const getIpAddressParams = (request: IncomingMessage): IpAddressType => {
    return {
        ipAddress: getIpAddressFromRequest(request),
    };
};

export { extractIpFromHeader, getIpAddressFromRequest, getIpAddressParams };
